<template>
  <v-container fluid class="payment-list-page pa-0">
    <v-card :loading="loading" outlined class="payment-list-card">
      <div class="payment-list-header">
        <div>
          <div class="page-title">Payment</div>
          <div class="page-subtitle">
            {{ payments.length }} payment{{ payments.length === 1 ? "" : "s" }} found
          </div>
        </div>

        <v-btn
          v-if="checkRightStatus(31) && CustomerId"
          small
          rounded
          color="primary"
          dark
          @click="add"
        >
          <v-icon left small>mdi-plus</v-icon>
          New Payment
        </v-btn>
      </div>

      <div class="payment-filter-bar">
        <v-text-field
          v-model="search"
          outlined
          prepend-inner-icon="search"
          autofocus
          dense
          label="Search payment"
          single-line
          hide-details
          clearable
          class="payment-search"
        />

        <v-spacer />

        <DateTimePicker
          v-if="!showAll"
          defaultRange="today"
          :range="true"
          v-model="dateTime"
          @submit="getList"
        />
      </div>

      <v-card-text class="pa-4">
        <div class="summary-row mb-4">
          <div class="summary-box">
            <div class="summary-label">List Total</div>
            <div class="summary-value">{{ total | currency }}</div>
          </div>

          <div class="summary-box">
            <div class="summary-label">Result Total</div>
            <div class="summary-value">{{ fullTotal | currency }}</div>
          </div>
        </div>

        <v-data-table
          :mobile-breakpoint="0"
          :headers="headers"
          :items="payments"
          :search="search"
          :loading="loading"
          @current-items="updateTotal"
          :sort-by="sortBy"
          sort-desc
          :page.sync="page"
          :expanded.sync="expanded"
          show-expand
          :items-per-page="itemsPerPage"
          class="payment-table"
        >
          <template v-slot:item="{ item, isExpanded, expand }">
            <tr class="pointer payment-row">
              <td class="text-left">
                <v-btn icon small @click="expand(!isExpanded)">
                  <v-icon>
                    {{ isExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                  </v-icon>
                </v-btn>
              </td>

              <td class="text-left">
                {{ item.date | moment("MM/DD/YYYY hh:mm A") }}
              </td>

              <td class="text-left font-weight-bold">
                {{ item.localId }}
              </td>

              <td class="text-left">
                {{ item.note || "No note" }}
              </td>

              <td class="text-right font-weight-bold">
                {{ item.total | currency }}
              </td>

              <td class="justify-end align-center text-right">
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon small>
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list dense class="menu-button">
                    <v-list-item v-if="!CustomerId" @click="viewProfile(item)">
                      <v-list-item-title>Profile</v-list-item-title>
                    </v-list-item>

                    <v-divider v-if="!CustomerId" />

                    <v-list-item @click="viewpayment(item)">
                      <v-list-item-title>View Payment</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>

          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-0">
              <div class="expanded-panel">
                <PaymentDetail :PaymentId="item.id" :CustomerId="CustomerId" />
              </div>
            </td>
          </template>

          <v-alert slot="no-results" :value="true" color="error" icon="warning">
            Your search for "{{ search }}" found no results.
          </v-alert>

          <template v-slot:body.append v-if="payments.length > 0">
            <tr class="table-total-row">
              <td colspan="4" class="text-right font-weight-bold">
                List Total
              </td>

              <td class="text-right font-weight-bold">
                {{ total | currency }}
              </td>

              <td></td>
            </tr>

            <tr class="table-result-row">
              <td colspan="4" class="text-right font-weight-bold">
                Result Total
              </td>

              <td class="text-right font-weight-bold">
                {{ fullTotal | currency }}
              </td>

              <td></td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import paymentService from "../service";
import PaymentDetail from "./PaymentDetailList";
import { mapGetters } from "vuex";

const DateTimePicker = () => import("@/components/DateTimePicker");

export default {
  name: "payment-list",

  props: {
    CustomerId: {
      default: null,
    },
    showAll: {
      default: false,
    },
    due: {
      default: false,
    },
  },

  components: {
    DateTimePicker,
    PaymentDetail,
  },

  data() {
    return {
      fullTotal: 0,
      total: 0,
      expanded: [],
      singleExpand: true,
      dateTime: {
        start: this.$moment().startOf("day").toISOString(),
        end: this.$moment().endOf("day").toISOString(),
      },
      createDialog: false,
      headers: [
        { text: "", value: "data-table-expand", sortable: false },
        {
          text: this.$t("labels.date"),
          align: "left",
          value: "date",
          sortable: true,
        },
        {
          text: "Payment Number",
          align: "left",
          value: "localId",
          sortable: true,
        },
        {
          text: "Note",
          align: "left",
          value: "note",
          sortable: true,
        },
        {
          text: "Total",
          value: "total",
          align: "right",
          sortable: true,
        },
        {
          text: "",
          value: "action",
          sortable: false,
        },
      ],
      payments: [],
      search: null,
      loading: true,
      page: 1,
      sortBy: "localId",
      pageCount: 0,
      itemsPerPage: 10,
    };
  },

  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),
  },

  created() {
    this.getList();
  },

  methods: {
    updateTotal(rows) {
      this.total = rows.reduce((sum, row) => {
        return sum + Number(row.total || 0);
      }, 0);
    },

    add() {
      this.createDialog = true;
    },

    viewProfile(payment) {
      this.$router.push({ path: `/customer/${payment.CustomerId}/profile` });
    },

    viewpayment(payment) {
      const routeData = this.$router.resolve({
        path: `/global/payment/${payment.id}/view`,
      });

      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },

    viewPackingSlip(payment) {
      const routeData = this.$router.resolve({
        path: `/global/payment/${payment.id}/packingSlip`,
      });

      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },

    async getList() {
      this.loading = true;

      const where = {};

      if (!this.showAll) {
        where.start = this.dateTime.start;
        where.end = this.dateTime.end;
      }

      if (this.CustomerId) {
        where.CustomerId = this.CustomerId;
      }

      if (this.due) {
        where.due = this.due;
      }

      try {
        const response = await paymentService.getAll(where);

        this.payments = (response.data || []).map((payment) => {
          const total = (payment.PaymentDetails || []).reduce((sum, detail) => {
            return sum + Number(detail.amount || 0);
          }, 0);

          return {
            ...payment,
            total,
          };
        });

        this.fullTotal = this.payments.reduce((sum, payment) => {
          return sum + Number(payment.total || 0);
        }, 0);

        return response;
      } catch (error) {
        console.error("payment getList failed", error);
        this.payments = [];
        this.total = 0;
        this.fullTotal = 0;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.payment-list-page {
  background: #f8fafc;
}

.payment-list-card {
  border-radius: 20px;
  background: #ffffff;
  overflow: hidden;
}

.payment-list-header {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
}

.page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.payment-filter-bar {
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.payment-search {
  max-width: 340px;
  min-width: 260px;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 220px));
  gap: 12px;
}

.summary-box {
  background: #f8fafc;
  border-radius: 16px;
  padding: 14px;
}

.summary-label {
  font-size: 12px;
  color: #64748b;
}

.summary-value {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin-top: 4px;
}

.payment-table {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.payment-row:hover {
  background: #f8fafc;
}

.expanded-panel {
  background: #f8fafc;
  padding: 14px;
  border-top: 1px solid #e2e8f0;
}

.table-total-row {
  background: #f8fafc;
}

.table-result-row {
  background: #eef2ff;
}

@media (max-width: 600px) {
  .payment-search {
    max-width: 100%;
    min-width: 100%;
  }

  .summary-row {
    grid-template-columns: 1fr;
  }
}
</style>